// ── requestsHelpers.js ───────────────────────────────────────────────────────
//
// Product request storage and utilities — Phase 1.
// Storage: localStorage only. No webhooks, no Make.com, no backend.
//
// Two signal types:
//   search_miss   — auto-captured when a search returns 0 results (session-deduped)
//   product_page  — user-triggered from a missing-store row on the product page
//
// ────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'cms_product_requests';
const SESSION_KEY = 'cms_search_miss_session'; // sessionStorage: dedup within browser tab

// ── Storage ───────────────────────────────────────────────────────────────────

export function getAllRequests() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
}

function saveRequests(reqs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reqs));
}

// ── Name normalisation ────────────────────────────────────────────────────────
// Lowercase + trim + collapse whitespace + naive singular (strip trailing -s).
// Good enough for grouping cucumber / cucumbers, tablet / tablets.

export function normalizeName(name) {
  return (name || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/s$/, '');
}

// ── ID generator ──────────────────────────────────────────────────────────────

function genId() {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── Session dedup for search misses ──────────────────────────────────────────
// A user typing "cucumber" 12 times in one session produces 1 signal, not 12.

function getSessionMisses() {
  try { return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]')); }
  catch { return new Set(); }
}

function markSessionMiss(normalizedName) {
  const s = getSessionMisses();
  s.add(normalizedName);
  sessionStorage.setItem(SESSION_KEY, JSON.stringify([...s]));
}

// ── logSearchMiss ─────────────────────────────────────────────────────────────
//
// Auto-called when search returns 0 results.
// Silent: logs to localStorage, does not require user interaction.
// Deduped: only fires once per normalizedName per browser tab session.

export function logSearchMiss(productName) {
  if (!productName?.trim()) return;
  const normalizedProductName = normalizeName(productName);
  if (getSessionMisses().has(normalizedProductName)) return;
  markSessionMiss(normalizedProductName);

  const reqs = getAllRequests();
  reqs.unshift({
    requestId:            genId(),
    productName:          productName.trim(),
    normalizedProductName,
    requestType:          'new_family',
    source:               'search_miss',
    timestamp:            new Date().toISOString(),
    status:               'pending',
  });
  saveRequests(reqs);
}

// ── logProductRequest ─────────────────────────────────────────────────────────
//
// User-triggered from product page (missing store row).
// Fields:
//   productName            — display name, required
//   requestType            — 'missing_store' | 'missing_brand' | 'new_family'
//   store                  — Tesco | Sainsburys | Aldi | Lidl (optional)
//   brand                  — optional free text
//   size                   — optional free text
//   source                 — 'product_page' (default)
//   requestedFromProductId — productId of the parent product family (optional)

export function logProductRequest({
  productName,
  requestType = 'missing_store',
  store,
  brand,
  size,
  source = 'product_page',
  requestedFromProductId,
}) {
  if (!productName?.trim()) return;
  const normalizedProductName = normalizeName(productName);
  const reqs = getAllRequests();
  reqs.unshift({
    requestId:            genId(),
    productName:          productName.trim(),
    normalizedProductName,
    requestType,
    ...(store                  ? { store }                  : {}),
    ...(brand                  ? { brand }                  : {}),
    ...(size                   ? { size }                   : {}),
    ...(requestedFromProductId ? { requestedFromProductId } : {}),
    source,
    timestamp: new Date().toISOString(),
    status:    'pending',
  });
  saveRequests(reqs);
}

// ── updateGroupStatus ─────────────────────────────────────────────────────────
//
// Updates status for ALL requests sharing a normalizedProductName.
// Status options: pending | researching | approved | added | rejected

export function updateGroupStatus(normalizedProductName, status) {
  const reqs    = getAllRequests();
  const updated = reqs.map(r =>
    r.normalizedProductName === normalizedProductName ? { ...r, status } : r
  );
  saveRequests(updated);
  return updated;
}

// ── getGroupedRequests ────────────────────────────────────────────────────────
//
// Aggregates raw requests into grouped summaries for the admin view.
// Returns array sorted by signalCount descending.
//
// Group shape:
// {
//   normalizedProductName  — grouping key
//   productName            — display name (first seen)
//   signalCount            — total number of signals
//   sources                — { search_miss: n, product_page: n }
//   status                 — highest-priority status across all items
//   firstSeenAt            — ISO timestamp
//   lastSeenAt             — ISO timestamp
//   items                  — raw Request[] newest first
// }

// When a group has mixed statuses, the most actionable one wins.
const STATUS_PRIORITY = ['researching', 'approved', 'added', 'rejected', 'pending'];

export function getGroupedRequests() {
  const reqs   = getAllRequests();
  const groups = {};

  for (const r of reqs) {
    const key = r.normalizedProductName;
    if (!groups[key]) {
      groups[key] = {
        normalizedProductName: key,
        productName:           r.productName,
        signalCount:           0,
        sources:               {},
        status:                'pending',
        firstSeenAt:           r.timestamp,
        lastSeenAt:            r.timestamp,
        items:                 [],
      };
    }
    const g = groups[key];
    g.signalCount++;
    g.sources[r.source] = (g.sources[r.source] || 0) + 1;

    // Highest-priority status wins
    if (STATUS_PRIORITY.indexOf(r.status) < STATUS_PRIORITY.indexOf(g.status)) {
      g.status = r.status;
    }
    if (r.timestamp < g.firstSeenAt) g.firstSeenAt = r.timestamp;
    if (r.timestamp > g.lastSeenAt)  g.lastSeenAt  = r.timestamp;
    g.items.push(r);
  }

  return Object.values(groups).sort((a, b) => b.signalCount - a.signalCount);
}

// ── getPendingCount ────────────────────────────────────────────────────────────
// Quick read for badge display — groups that are not added or rejected.

export function getPendingCount() {
  return getGroupedRequests().filter(
    g => !['added', 'rejected'].includes(g.status)
  ).length;
}
